/**
 * Event Pricing Settings Component
 * 活动价格设置组件 - 管理员端
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface PricingSettingsProps {
  eventId?: number;
  initialData?: EventPricingData;
  onSave: (data: EventPricingData) => Promise<void>;
}

interface EventPricingData {
  is_paid_event: boolean;
  currency: string;
  
  // 早鸟价格
  early_bird_enabled: boolean;
  early_bird_price: number;
  early_bird_start_date: string;
  early_bird_end_date: string;
  
  // 正常价格
  regular_price: number;
  price_end_date: string;
  
  // Token 门控
  token_gating_enabled: boolean;
  token_symbol: string;
  token_contract_address: string;
  token_min_holding: number;
  token_holders_only: boolean;
}

export default function PricingSettings({ 
  eventId, 
  initialData,
  onSave 
}: PricingSettingsProps) {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  
  const [formData, setFormData] = useState<EventPricingData>({
    is_paid_event: false,
    currency: 'USD',
    early_bird_enabled: false,
    early_bird_price: 0,
    early_bird_start_date: '',
    early_bird_end_date: '',
    regular_price: 0,
    price_end_date: '',
    token_gating_enabled: false,
    token_symbol: 'GTRA',
    token_contract_address: '',
    token_min_holding: 0,
    token_holders_only: false,
  });
  
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // 加载初始数据
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);
  
  // 更新字段
  const updateField = <K extends keyof EventPricingData>(
    field: K,
    value: EventPricingData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };
  
  // 保存设置
  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(formData);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save pricing settings:', error);
    } finally {
      setSaving(false);
    }
  };
  
  // 货币选项
  const currencies = [
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'CNY', label: 'CNY - Chinese Yuan' },
    { value: 'USDC', label: 'USDC - USD Coin' },
  ];
  
  return (
    <div className="space-y-8">
      {/* 基础设置 */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          {isZh ? '基础设置' : 'Basic Settings'}
        </h3>
        
        <div className="space-y-4">
          {/* 是否付费活动 */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-700">
                {isZh ? '付费活动' : 'Paid Event'}
              </label>
              <p className="text-sm text-gray-500">
                {isZh ? '启用此选项允许收取报名费用' : 'Enable to charge registration fees'}
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_paid_event}
                onChange={(e) => updateField('is_paid_event', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          
          {/* 货币选择 */}
          {formData.is_paid_event && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isZh ? '货币' : 'Currency'}
              </label>
              <select
                value={formData.currency}
                onChange={(e) => updateField('currency', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {currencies.map(curr => (
                  <option key={curr.value} value={curr.value}>
                    {curr.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>
      
      {/* 早鸟价格设置 */}
      {formData.is_paid_event && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">
              {isZh ? '早鸟价格' : 'Early Bird Price'}
            </h3>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.early_bird_enabled}
                onChange={(e) => updateField('early_bird_enabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          
          {formData.early_bird_enabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isZh ? '早鸟价格' : 'Early Bird Price'}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.early_bird_price}
                  onChange={(e) => updateField('early_bird_price', parseFloat(e.target.value) || 0)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isZh ? '开始日期' : 'Start Date'}
                </label>
                <input
                  type="date"
                  value={formData.early_bird_start_date}
                  onChange={(e) => updateField('early_bird_start_date', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isZh ? '结束日期' : 'End Date'}
                </label>
                <input
                  type="date"
                  value={formData.early_bird_end_date}
                  onChange={(e) => updateField('early_bird_end_date', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* 正常价格设置 */}
      {formData.is_paid_event && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {isZh ? '正常价格' : 'Regular Price'}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isZh ? '正常价格' : 'Regular Price'}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.regular_price}
                onChange={(e) => updateField('regular_price', parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isZh ? '价格结束日期' : 'Price End Date'}
              </label>
              <input
                type="date"
                value={formData.price_end_date}
                onChange={(e) => updateField('price_end_date', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                {isZh ? '留空表示价格持续有效' : 'Leave empty for ongoing pricing'}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Token 门控设置 */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              {isZh ? 'Token 免费票' : 'Token Free Ticket'}
            </h3>
            <p className="text-sm text-gray-500">
              {isZh ? '允许持有特定 Token 的用户免费参加' : 'Allow users holding specific tokens to attend for free'}
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.token_gating_enabled}
              onChange={(e) => updateField('token_gating_enabled', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
        
        {formData.token_gating_enabled && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="font-medium text-gray-700">
                  {isZh ? '仅限 Token 持有者' : 'Token Holders Only'}
                </label>
                <p className="text-sm text-gray-500">
                  {isZh ? '启用后只有 Token 持有者可以报名' : 'Only token holders can register when enabled'}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.token_holders_only}
                  onChange={(e) => updateField('token_holders_only', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isZh ? 'Token 符号' : 'Token Symbol'}
                </label>
                <input
                  type="text"
                  value={formData.token_symbol}
                  onChange={(e) => updateField('token_symbol', e.target.value)}
                  placeholder="GTRA"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isZh ? '最低持有数量' : 'Minimum Holding'}
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.token_min_holding}
                  onChange={(e) => updateField('token_min_holding', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isZh ? '合约地址 (可选)' : 'Contract Address (Optional)'}
                </label>
                <input
                  type="text"
                  value={formData.token_contract_address}
                  onChange={(e) => updateField('token_contract_address', e.target.value)}
                  placeholder="0x..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* 保存按钮 */}
      <div className="flex items-center justify-end gap-4">
        {saved && (
          <span className="text-green-600 text-sm flex items-center gap-1">
            <i className="ri-check-line"></i>
            {isZh ? '已保存' : 'Saved'}
          </span>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-6 py-2 rounded-lg font-medium text-white transition-colors ${
            saving
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              {isZh ? '保存中...' : 'Saving...'}
            </span>
          ) : (
            isZh ? '保存价格设置' : 'Save Pricing Settings'
          )}
        </button>
      </div>
    </div>
  );
}
